import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { NavLinkProps } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { WithCrmHostUrlContext } from '../../resource-redirect/crm-host-url-context';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { mainNavigationItems, IMainNavigationItem } from './main-navigation-items';
import { Divider } from '../../divider';
import { useFeatureToggles } from '../../../feature-toggles';
import { useAllowedActions } from '../../../authentication';
import { getFilteredItems } from './get-filtered-items';
import { MainNavigationSubMenu } from './main-navigation-sub-menu';
import { MainNavigationNavMenu } from './main-navigation-nav-menu';

import { useTenantAddons } from '../../tenant-addons/tenant-addons-provider';
import { useServiceProviderAreaAccess } from '../../../areas/citizen/serviceprovider/hooks/use-service-provider-area-access';
import { useRoleThemes } from '../../../authentication/use-role-themes';
import { attendanceAreaPathName } from '../../../areas/attendance/attendance-area-pathnames';
import { effortsAreaPathName } from '../../../areas/efforts/efforts-area-pathnames';

const useStyles = makeStyles()({
  divider: {
    marginTop: 10,
    marginRight: '-8px',
    marginBottom: 10,
    marginLeft: '-8px',
  },
});
export function MainNavigation() {
  const { classes } = useStyles();
  const loc = useLocation();
  const { isFeatureEnabled } = useFeatureToggles();
  const tenantAddons = useTenantAddons();

  const [anchorEl, setAnchorEl] = useState<{ anchor?: EventTarget & HTMLButtonElement; guid: string }>();

  function handleClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, guid: string) {
    if (anchorEl?.anchor !== event.currentTarget) {
      setAnchorEl({ ...anchorEl, anchor: event.currentTarget, guid });
    }
  }

  function handleClose() {
    setAnchorEl({ ...anchorEl, anchor: undefined, guid: '' });
  }

  function isActive(item: IMainNavigationItem) {
    const { pathname } = loc;

    if (pathname.indexOf(`/${item.url}`) === 0) return true;
    if (item.activeOnChildPages && item.children?.some(child => child.url && pathname.indexOf(`/${child.url}`) === 0)) return true;
    // Custom rule for "citizen" and "citizens" routes
    if (pathname.indexOf('/citizen') === 0 && item.url.indexOf('citizen') === 0) return true;
    // Attendance pages belong to the "efforts" menu only while it still shows the attendance children
    if (pathname.indexOf(`/${attendanceAreaPathName}`) === 0 && item.url === effortsAreaPathName) return hasAttendanceChildren(item);

    if (item.url === pathname.split('/')[1] || item.url === pathname) return true;
    return false;
  }

  const allowedActions = useAllowedActions();
  const hasServiceProviderAreaAccess = useServiceProviderAreaAccess();
  const { isServiceProvider } = useRoleThemes();

  const filteredItems = getFilteredItems(mainNavigationItems, tenantAddons, isFeatureEnabled, allowedActions, hasServiceProviderAreaAccess, isServiceProvider);

  return (
    <nav className="layout__main-navigation__nav">
      <WithCrmHostUrlContext configurationClient={moduleClientFactory.createConfigurationClient()}>
        {filteredItems.map((item: IMainNavigationItem) => {
          // Temporary open crm button in left menu
          const isCrmLink = item.guid === '950c8062-f130-43d9-9469-ab55eed6f749';
          return (
            <div key={item.guid} style={{ marginTop: isCrmLink ? 'auto' : '' }}>
              {item.split ? <Divider className={classes.divider} /> : null}
              {!item.children || item.children.length === 0 ? (
                <MainNavigationNavMenu item={item} isActive={isActive(item)} />
              ) : (
                <MainNavigationSubMenu item={item} isActive={isActive(item)} onClick={handleClick} anchorEl={anchorEl} onClose={handleClose} />
              )}
            </div>
          );
        })}
      </WithCrmHostUrlContext>
    </nav>
  );
}

function hasAttendanceChildren(item: IMainNavigationItem) {
  return item.children?.some(child => child.url?.indexOf(attendanceAreaPathName) === 0) ?? false;
}
